import { useMutation } from '@apollo/client';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChangeReviewModal } from './ChangeCards.tsx';
import { ConfigChange, ConfigChannel, formatConfigDate, toConfigChanges } from './changes.ts';
import { CHANNEL_CONFIG_REFETCH_QUERIES, IMPORT_CHANNEL_CONFIG, PREVIEW_CHANNEL_CONFIG_IMPORT } from './queries.ts';
import DropArea from '../../../core/components/DropArea.tsx';
import Modal from '../../../core/components/Modal.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';

interface ImportPreview {
    exportedFromChannelName?: string | null;
    exportedFromCustomerName?: string | null;
    exportedAt?: string | null;
    exportedByName?: string | null;
    changes: ConfigChange[];
}

interface FilePreview {
    file: File;
    preview: ImportPreview;
}

interface ImportConfigModalProps {
    channel: ConfigChannel;
    onClose: () => void;
}

const ImportConfigModal = ({ channel, onClose }: ImportConfigModalProps) => {
    const { t } = useTranslation('config');
    const { publishToast } = useToaster();
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<FilePreview | null>(null);
    const [previewImport, { loading }] = useMutation(PREVIEW_CHANNEL_CONFIG_IMPORT);
    const [importConfig] = useMutation(IMPORT_CHANNEL_CONFIG, { refetchQueries: CHANNEL_CONFIG_REFETCH_QUERIES });
    const variables = { documentType: channel.documentType, channelId: channel.channelId };

    const handleFile = async (droppedFile: File) => {
        setFile(droppedFile);
        try {
            const { data } = await previewImport({ variables: { ...variables, file: droppedFile } });
            const { changes, ...exportedFrom } = data.previewChannelConfigImport;
            setPreview({ file: droppedFile, preview: { ...exportedFrom, changes: toConfigChanges(changes) } });
        } catch (error) {
            console.error(error);
            setFile((current) => (current === droppedFile ? null : current));
            publishToast({ description: t('configPage.history.import.invalidFile'), status: 'error' });
        }
    };

    if (file && preview?.file === file) {
        const { exportedFromChannelName, exportedFromCustomerName, exportedAt, exportedByName, changes } =
            preview.preview;
        return (
            <ChangeReviewModal
                title={t('configPage.history.import.titleWithFile', { name: file.name })}
                description={t('configPage.history.import.exportedFrom', {
                    details: [
                        exportedFromChannelName,
                        exportedFromCustomerName,
                        exportedAt && formatConfigDate(exportedAt),
                        exportedByName,
                    ]
                        .filter(Boolean)
                        .join(' · '),
                })}
                parts={channel.parts}
                changes={changes}
                empty={t('configPage.history.import.empty')}
                submitKey="configPage.history.import.submit"
                onSubmit={async (paths) => {
                    await importConfig({ variables: { ...variables, file, paths } });
                    channel.onConfigChange();
                    return t('configPage.history.import.success', { count: paths.length });
                }}
                onClose={onClose}
            />
        );
    }

    return (
        <Modal title={t('configPage.history.import.title')} onClose={onClose}>
            <div className="flex w-[40rem] max-w-full flex-col gap-4 pb-6">
                <DropArea
                    placeholder={t('configPage.history.import.drop')}
                    accept={{ 'application/json': ['.json'] }}
                    maxFiles={1}
                    files={file ? [file] : []}
                    onFilesChange={(files) => (files[0] ? handleFile(files[0]) : setFile(null))}
                />
                {loading && file && (
                    <span className="text-sm text-content-medium">{t('configPage.history.import.reading')}</span>
                )}
            </div>
        </Modal>
    );
};

export default ImportConfigModal;
